import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { Button, ConfigProvider, DatePicker, Modal, notification } from 'antd';
import { ProColumns } from '@ant-design/pro-components';
import enUSIntl from 'antd/es/locale/en_US';
import { ProTable } from '@ant-design/pro-table';
import axiosClient from '../api/axiosClient';
import { GlobalStateContext } from '../context/GlobalContext';
import axios, { CancelTokenSource } from 'axios';
import dayjs, { Dayjs } from 'dayjs';

const currencyMap: any = { 43: '€', 46: '£', 142: '$' };

export interface Props {
    platform?: string;
    metric?: string;
    dimension?: string;
    startDate: string;
    endDate: string;
    managed_customers: number[];
    metric_type: string;
    visible?: boolean;
    selectedIds: number[];
    valMeasurement?: string;
}

type ExtensiveDonutParams = {
    metric: number;
    dimension: string;
};

// DataSource
export type DataSourceType = {
    data: ExtensiveDonutParams;
    total: number;
    currency: number;
};

const ExtensiveDonutData = (props: Props) => {
    const [search, setSearch] = useState<string>('');
    const [dataSource, setDataSource] = useState<DataSourceType>({
        data: { metric: 0, dimension: '' },
        total: 0.0,
        currency: 43
    });
    const { isExtensiveModalVisible, setIsExtensiveModalVisible } = useContext(GlobalStateContext);

    // Todo maybe cancelToken is not necessary
    const [cancelToken, setCancelToken] = useState<CancelTokenSource | null>(null);

    function handleOk() {
        setIsExtensiveModalVisible!(false);
    }

    function handleCancel() {
        setIsExtensiveModalVisible!(false);
    }

    const [pageSize, setPageSize] = useState<number>(6);

    // ── Export state ─────────────────────────────────────────────────────────
    const [exportModalVisible, setExportModalVisible] = useState<boolean>(false);
    const [exportLoading, setExportLoading] = useState<boolean>(false);
    const [exportDateRange, setExportDateRange] = useState<[Dayjs, Dayjs] | null>(null);

    // Pre-fill the export date range whenever the main filter dates change
    useEffect(() => {
        if (props.startDate && props.endDate) {
            setExportDateRange([dayjs(props.startDate), dayjs(props.endDate)]);
        }
    }, [props.startDate, props.endDate]);

    const handleExport = async () => {
        if (!exportDateRange || exportDateRange.length < 2) {
            notification.warning({ message: 'Please select a date range' });
            return;
        }
        setExportLoading(true);
        try {
            const res = await axiosClient.portal.post(
                '/api/employees/exportextensivedata',
                {
                    platform: props.platform,
                    dimension: props.dimension,
                    metric_type: props.metric_type,
                    managed_customers: props.managed_customers,
                    startDate: exportDateRange[0].format('YYYY-MM-DD'),
                    endDate: exportDateRange[1].format('YYYY-MM-DD'),
                },
                { responseType: 'blob' }
            );
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `export_${props.platform}_${props.dimension}_${dayjs().format('YYYYMMDD')}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            setExportModalVisible(false);
        } catch (e: any) {
            notification.error({ message: 'Export failed. Please try again.' });
        } finally {
            setExportLoading(false);
        }
    };

    const columns: ProColumns<ExtensiveDonutParams>[] = [
        { 
            title: props.dimension, 
            dataIndex: 'dimension', 
            align: 'center', 
            width: props.dimension === 'Customer' && props.metric === 'Total Duration' && props.valMeasurement === 'mins' ? '50%' : '66%' 
        },
        {
            title: props.metric,
            dataIndex: 'metric',
            align: 'center',
            width: props.dimension === 'Customer' && props.metric === 'Total Duration' && props.valMeasurement === 'mins' ? '33%' : '33%',
            render: (dom, entity) => {
                return formatValue(entity.metric) + (props.valMeasurement ? props.valMeasurement : '');
            }
        },
        ...(props.platform === 'Jwplayer' ? [{
            title: 'Percentage',
            dataIndex: 'metric',
            align: 'center' as const,
            width: '17%',
            render: (dom: any, entity: ExtensiveDonutParams) => {
                const percentage = dataSource.total > 0 ? ((entity.metric / dataSource.total) * 100).toFixed(2) : '0.00';
                return `${percentage}%`;
            }
        }] : [])
    ];

    function formatValue(value: number): string {
        let finalValue: number = Math.round(value * 100) / 100;
        let returnValue: string;

        if (props.metric == 'Revenue') {
            returnValue = (finalValue?.toLocaleString('el-GR') || 0) + ' ' + currencyMap[dataSource.currency];
        } else {
            returnValue = finalValue?.toLocaleString('el-GR') || '0';
        }

        return returnValue;
    }

    return (
        <Modal
            title='Detailed View'
            open={props.visible}
            onOk={handleOk}
            onCancel={handleCancel}
            width={1100}
            bodyStyle={{ height: 470, overflowY: 'auto' }}
            closable={true}
            footer={null}
            mask={true}>
            <ConfigProvider locale={enUSIntl}>
                <ProTable<ExtensiveDonutParams>
                    headerTitle={'Total: ' + formatValue(dataSource!.total) + (props.valMeasurement ? props.valMeasurement : '')}
                    params={{
                        keyword: search
                    }}
                    request={async (params, sort, filter) => {
                        try {
                            // Cancel the previous request if it's still running
                            if (cancelToken) {
                                cancelToken.cancel('Request canceled');
                            }

                            const newCancelToken = axios.CancelToken.source();
                            setCancelToken(newCancelToken);

                            const res = await axiosClient.portal.post(
                                '/api/employees/getextensivedata',
                                {
                                    ...props,
                                    keyword: params.keyword
                                },
                                { cancelToken: newCancelToken.token }
                            );

                            let tempDataSource = JSON.parse(JSON.stringify(res.data));

                            setDataSource(tempDataSource);
                            // setLoading(false)

                            return { data: tempDataSource.data, success: true };
                        } catch (e: any) {
                            notification.error({ message: e.response.data.message });
                            // setLoading(false)
                            return { data: [], success: false };
                        }
                    }}
                    columns={columns}
                    rowKey='id'
                    // Pagination PageSize Fix (pagination fix, pagination bug fix)
                    pagination={{
                        pageSize,
                        hideOnSinglePage: false,
                        showQuickJumper: true,
                        pageSizeOptions: ['6', '10', '20', '50'], // Optional: Provide available page sizes
                        onShowSizeChange: (current, size) => setPageSize(size), // Update pageSize on change
                        showSizeChanger: true
                    }}
                    options={{
                        search: {
                            value: search,
                            onChange: (value) => {
                                setSearch(value.target.value);
                            },
                            allowClear: true,
                            style: { width: 300 }
                        }
                    }}
                    debounceTime={700}
                    search={false}
                    bordered
                    toolBarRender={() => [
                        // if props.platform is Jwplayer, disable export button
                        // disabled={props.platform === 'Jwplayer'}
                        <Button
                            key='export'
                            type='default'
                            onClick={() => setExportModalVisible(true)}
                            >
                            Export Excel
                        </Button>
                    ]}
                />
            </ConfigProvider>

            {/* ── Export date-range modal ────────────────────────────────── */}
            <Modal
                title={`Export — ${props.platform} / ${props.dimension}`}
                open={exportModalVisible}
                onOk={handleExport}
                onCancel={() => setExportModalVisible(false)}
                okText='Export'
                confirmLoading={exportLoading}
                width={420}>
                <p style={{ marginBottom: 8 }}>Select date range for export:</p>
                <DatePicker.RangePicker
                    value={exportDateRange}
                    onChange={(dates) =>
                        setExportDateRange(dates as [Dayjs, Dayjs] | null)
                    }
                    format='YYYY-MM-DD'
                    style={{ width: '100%' }}
                />
            </Modal>
        </Modal>
    );
};

export default ExtensiveDonutData;
